<template>
  <network-button-window
    :window-name="$tc('Calculation', 2)"
    :parent-toggle-window="windowOpen"
    @toggleWindow="restartOnWindowOpen"
    :disabled="buttonsDisabled"
  >
    <template v-slot:buttonIcon>
      <CalculationsSVG class="calculations-icon" />
    </template>

    <template v-slot:windowContent>
      <div class="processes-inner-container">
        <process-item
          name="All"
          :selected="all.checked"
          :loading="runLoading"
          :selection="runSelection"
          :run="runLoading"
          @toggle="toggleAllProcesses"
          :all-checkbox-is-disabled="allCheckboxIsDisabled"
        />
        <process-item
          v-for="item in processes"
          :key="item.name"
          :name="item.name"
          :selected="item.state.checked"
          :loading="item.state.loading"
          :complete="item.state.result"
          :selection="runSelection"
          :run="runLoading"
          :permissionName="item.permission"
          @toggle="item.state.checked = $event"
        />
      </div>

      <processes-button
        v-if="runSelection"
        buttonName="Run"
        :disabled="!runEnabled"
        @onClick="runProcesses"
      />

      <!--      <processes-button
        v-if="!runSelection && runLoading && !runComplete"
        buttonName="Cancel"
        style="background: white; color: var(&#45;&#45;primary-highlight); border: 1px solid var(&#45;&#45;primary-highlight)"
        @onClick="cancel"
      />-->
      <processes-button
        v-if="!runSelection && !runLoading && runComplete"
        buttonName="Restart"
        @onClick="restart"
      />
    </template>
  </network-button-window>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import CalculationsSVG from "@/assets/home/toolbars/designerToolbar/calculation.svg";
import ProcessItem from "./ProcessItem";
import PlaySVG from "@/assets/home/tabs/play.svg";
import CheckSVG from "@/assets/home/tabs/check.svg";
import ProcessesButton from "./ProcessesButton";
import DevTag from "@/components/_Atoms/DevTag";
import NetworkButtonWindow from "../NetworkButtonWindow";
import { layersMixin } from "@/mixins/layersMixin";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";

export default {
  name: "NetworkCalculations",

  components: {
    NetworkButtonWindow,
    DevTag,
    CalculationsSVG,
    ProcessItem,
    PlaySVG,
    CheckSVG,
    ProcessesButton
  },

  mixins: [layersMixin, checkPermissionsMixin],

  data() {
    return {
      /** Information about the Calculations run state: If it is in the Selection, Loading or Complete phase */
      runSelection: true,
      all: { checked: false },
      /** Information about the name and state of each process: If it is Checked, Running or Complete */
      processes: [
        {
          name: "Capacity",
          state: {
            checked: false,
            loading: false,
            result: undefined
          },
          method: "runCapacityCalculation",
          permission: "calculation_capacity_store",
          showInBidding: true
        },
        {
          name: "Splicing",
          state: {
            checked: false,
            loading: false,
            result: undefined
          },
          method: "runSplicingCalculation",
          permission: "calculation_splicing_store",
          showInBidding: false
        },
        {
          name: "Labelling",
          state: {
            checked: false,
            loading: false,
            result: undefined
          },
          method: "runLabellingCalculation",
          permission: "calculation_labelling_store",
          showInBidding: false
        },
        {
          name: "Clustering",
          state: {
            checked: false,
            loading: false,
            result: undefined
          },
          method: "runClusteringCalculation",
          permission: "calculation_cluster_store",
          showInBidding: false
        }
      ],
      windowOpen: false
    };
  },

  computed: {
    ...mapGetters({
      networkDiagramTechnicalLocalUUID: "getNetworkDiagramTechnicalLocalUUID"
    }),

    /**
     * Determines if the conditions are set for a Calculations run to start.
     * @returns {boolean}
     */
    runEnabled() {
      return this.nrCheckedItems > 0;
    },

    nrOfProcesses() {
      return this.processes.length;
    },

    /**
     * Determines the number of checked process items.
     * @returns {number}
     */
    nrCheckedItems() {
      let checkedItems = 0;
      for (let i = 0; i < this.nrOfProcesses; i++) {
        if (this.processes[i].state.checked) checkedItems++;
      }
      if (checkedItems === 0) {
        this.all.checked = false;
      }
      if (checkedItems === this.nrOfProcesses) {
        this.all.checked = true;
      }
      return checkedItems;
    },

    workspaceUUID() {
      let workspaceUUID = this.$store.getters.getNetworkDiagramWorkspaceUUID;
      if (!workspaceUUID) {
        workspaceUUID = this.$store.getters.workspace.uuid;
      }
      return workspaceUUID;
    },

    buttonsDisabled() {
      return this.networkDiagramTechnicalLocalUUID === "Technical Local" ||  !this.networkDiagramTechnicalLocalUUID;
    },

    runLoading() {
      for (let i = 0; i < this.nrOfProcesses; i++) {
        if (this.processes[i].state.loading) return true;
      }
      return false;
    },

    runComplete() {
      for (let i = 0; i < this.nrOfProcesses; i++) {
        if (
          this.processes[i].state.checked &&
          this.processes[i].state.result === undefined
        )
          return false;
      }
      return true;
    },

    isBidding(){
      return sessionStorage.getItem("is_bidding") === 'true';
    },

    allCheckboxIsDisabled() {
      let allIsDisabled = true;
      this.processes.forEach(process => {
        allIsDisabled = allIsDisabled && !this.userHasFibercloudPermission(process.permission)
      })
      return allIsDisabled;
    }
  },

  methods: {
    ...mapActions(["RUN_NETWORK_CALCULATION", "GET_NETWORK_DIAGRAM_DATA"]),


    /**
     * Get the uuid and the object type of the network draw
     */
    getCalculationsObjectAndUuid(){
        return {
          object_type: "technical_local",
          uuid: this.networkDiagramTechnicalLocalUUID
        }
    },

    /**
     * Toggles all the elements of the Processes list.
     * @param e
     */
    toggleAllProcesses(e) {
      for (let i = 0; i < this.nrOfProcesses; i++) {
        if (this.userHasFibercloudPermission(this.processes[i].permission)) {
          this.processes[i].state.checked = e;
        }
      }
    },

    /**
     * Initiates the run of the checked items of the Processes list.
     */
    runProcesses() {
      if (this.runEnabled) {
        this.runSelection = false;
        let processesToRun = [];
        for (let i = 0; i < this.nrOfProcesses; i++) {
          if (this.processes[i].state.checked) {
            processesToRun.push(i);
            this.processes[i].state.loading = true;
          }
        }
        this.runProcessesSequentially(processesToRun);
      }
    },

    runProcessesSequentially(processesToRun) {
      processesToRun.reduce(
        (promise, processIndex) =>
          promise
            .then(() => this[this.processes[processIndex].method](processIndex))
            .catch(() => {}),
        Promise.resolve()
      );
    },

    /**
     * Executes the action associated wih the selected process and updates the respective state object.
     * @param {Number} processIndex - index of the process being run
     * @param {*} data - data to send as a parameter of the action
     */
    executeProcessAction(processIndex, data) {
      let result;
      return new Promise((resolve, reject) => {
        this.RUN_NETWORK_CALCULATION(data)
          .then(() => {
            result = "success";
            resolve();
          })
          .catch(() => {
            result = "failure";
            reject();
          })
          .then(() => {
            this.processes[processIndex].state.loading = false;
            this.processes[processIndex].state.result = result;
          });
      });
    },

    /**
     * Runs the calculation of the capacity of the network starting in the given technical local.
     * @param {Number} i - index of the Capacity process
     */
    runCapacityCalculation(i) {
      let object_data = this.getCalculationsObjectAndUuid();
      let dataToSend = {
        workspace_uuid: this.workspaceUUID,
        object: {
          uuid: object_data.uuid,
          object_type: object_data.object_type
        }
      };
      return new Promise((resolve, reject) => {
        this.executeProcessAction(i, {
          endpoint: "networks/calculation",
          data: dataToSend
        })
          .then(() => resolve())
          .catch(() => reject());
      });
    },

    /**
     * Runs the calculation of the splicing of the network starting in the given technical local.
     * @param {Number} i - index of the Splicing process
     */
    runSplicingCalculation(i) {
      let object_data = this.getCalculationsObjectAndUuid();
      let dataToSend = {
        workspace_uuid: this.workspaceUUID,
        object: {
          uuid: object_data.uuid,
          object_type: object_data.object_type
        }
      };
      return new Promise((resolve, reject) => {
        this.executeProcessAction(i, {
          endpoint: "networks/splicing",
          data: dataToSend
        })
          .then(() => resolve())
          .catch(() => reject());
      });
    },

    /**
     * Runs the calculation of the capacity of the network starting in the given technical local.
     * @param {Number} i - index of the Capacity process
     */
    runLabellingCalculation(i) {
      let object_data = this.getCalculationsObjectAndUuid();
      return new Promise((resolve, reject) => {
        this.executeProcessAction(i, {
          endpoint: "networks/labellingNetwork",
          data: {
            object_type: object_data.object_type,
            uuid: object_data.uuid,
            activeLayers: this.getActiveLayersNamesList(
              JSON.parse(sessionStorage.getItem("active_layers"))
            ),
            workspaceUUID: this.workspaceUUID
          }
        })
          .then(() => resolve())
          .catch(() => reject());
      });
    },

    runClusteringCalculation(i) {
      let object_data = this.getCalculationsObjectAndUuid();
      let dataToSend = {
        workspace_uuid: this.workspaceUUID,
        object: {
          uuid: object_data.uuid,
          object_type: object_data.object_type
        }
      };
      return new Promise((resolve, reject) => {
        this.executeProcessAction(i, {
          endpoint: "closureszones/generate",
          data: dataToSend
        })
          .then(() => resolve())
          .catch(() => reject());
      });
    },

    /**
     * Resets all Calculations processes values.
     */
    restart() {
      for (let i = 0; i < this.nrOfProcesses; i++) {
        this.processes[i].state.result = undefined;
      }
      this.runSelection = true;
    },
    /**
     * If the window was opened (after being closes), reset the run state.
     * @param {boolean} windowIsOpen - if the Calculations window is open or not
     */
    restartOnWindowOpen(windowIsOpen) {
      this.windowOpen = windowIsOpen;
      if (this.runComplete && windowIsOpen && !this.buttonsDisabled) {
        this.restart();
      }
    },

    refresh() {
      this.GET_NETWORK_DIAGRAM_DATA({
        nodeUUID: this.networkDiagramTechnicalLocalUUID,
        workspaceUUID: this.workspaceUUID
      });
    }
  },
  mounted(){
    let self = this;
    this.processes = this.processes.filter((el) => {
      return !self.isBidding || el.showInBidding;
    })
  },
  watch: {
    runLoading(newValue) {
      this.$emit("updateRunState", newValue);
    },

    runComplete(newValue) {
      if (newValue) {
        for (let i = 0; i < this.nrOfProcesses; i++) {
          if (this.processes[i].state.result === "success") this.refresh();
        }
      }
    },

    networkDiagramTechnicalLocalUUID(newValue) {
      if (newValue === undefined) {
        this.windowOpen = false;
      }
    }
  }
};
</script>

<style scoped>
.calculations-icon {
  width: 25px;
}

.calculations-icon path:nth-child(2) {
  fill: var(--secondary-darker);
}

.processes-inner-container {
  width: 100%;
  padding: 5px 0;
}

.spacer {
  height: 38px;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Content/Tabs/Network/Calculations/Calculations.vue